/**
 * The background jobs the backend enqueues for a receiving-log submit (the slow
 * side-effects moved out of the synchronous request: GP note, photo uploads, emails).
 * `type` matches the backend's job-type names exactly.
 */
export type JobType =
  | 'Note'
  | 'ShipmentPhotos'
  | 'DamagePhotos'
  | 'SlipPhotos'
  | 'NotificationEmail'
  | 'ExpeditingEmail';

/**
 * One enqueued background job's live state, as returned on the submit response
 * (`AddReceivingLogResponse.jobs`) and the status read-back
 * (`ReceivingLogStatusResponse.jobs`).
 */
export interface SubmitJob {
  type: JobType;
  /** Backend job status, e.g. 'Pending' | 'InProgress' | 'Succeeded' | 'Failed'. */
  status: string;
  /** Last error message when a run failed; null otherwise. */
  lastError: string | null;
  attempts: number;
  maxAttempts: number;
  /** True once retries are exhausted — a human must act (re-enqueue). */
  isPermanentlyFailed: boolean;
  /**
   * For email jobs (`NotificationEmail` / `ExpeditingEmail`): whether that email was
   * configured to include photos, read from the job's payload. `null` for non-email jobs.
   * When `true`, re-sending after a photo failure adds real content (the photos); when
   * `false`, a re-send would just be a duplicate, so it isn't offered.
   */
  includesPhotos?: boolean | null;
}

/** Human-readable names for the background job types, for user-facing status/toasts. */
export const JOB_LABELS: Record<JobType, string> = {
  Note: 'Receiving note',
  ShipmentPhotos: 'Shipment photos',
  DamagePhotos: 'Damage photos',
  SlipPhotos: 'Packing slip',
  NotificationEmail: 'Notification email',
  ExpeditingEmail: 'Expediting email',
};
